import { isiOSDevice } from '@ixigo/pwa';
import { useRouter } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';
import { CbccLokiKey, logCbccLokiError } from '@/lib/loki-logger';
import { configureUI, isForeignEnvironment } from '@/lib/pwa';
import { handleCbccBack } from '../utils/cbcc-back';

/**
 * Routes BOTH ways out of the CVP page — the in-page control and the host's
 * DEVICE back press — through `handleCbccBack`, so the destination logic and the
 * Backpress event are identical whichever the user hits. Previously only the
 * in-page button was wired, and a device back left the page untracked.
 *
 * Foreign hosts only. ixiweb/iximweb are ordinary browsers whose own back
 * button we must not intercept, and there is no SDK bridge there to intercept
 * with.
 *
 * Android takes a handler. iOS gets `disabled` instead, matching the
 * manage-payments hook: the swipe-back gesture is the host's to own, and the
 * SDK offers no way to observe it — so on iOS the in-page control is the tracked
 * path, which is why this route always renders one inside a foreign webview.
 *
 * `handleBackPress` returns false to tell the host we have handled the press and
 * it should not also run its own default (which would double-navigate).
 */
export function useCbccBack() {
	const clientId = useRouter().options.context.clientConfig().clientId;
	const isForeign = !!isForeignEnvironment(clientId);

	useEffect(() => {
		if (!isForeign) return;

		const uiConfig: IxigoSDKUIConfig = isiOSDevice()
			? { backNavigationMode: { type: 'disabled' } }
			: {
					backNavigationMode: {
						type: 'handler',
						handleBackPress: () => {
							handleCbccBack(clientId);
							return false;
						},
					},
				};

		configureUI(uiConfig).catch((error) => {
			logCbccLokiError(CbccLokiKey.NATIVE_BACK_CONFIGURE_FAILED, error, {
				phase: 'mount',
			});
		});

		// Release the handler on unmount: it closes over this route's back
		// behaviour, and leaving it registered would apply CVP navigation to
		// whatever the host shows next.
		return () => {
			configureUI({ backNavigationMode: { type: 'disabled' } }).catch(
				(error) => {
					logCbccLokiError(CbccLokiKey.NATIVE_BACK_CONFIGURE_FAILED, error, {
						phase: 'cleanup',
					});
				},
			);
		};
	}, [isForeign, clientId]);

	const handleBack = useCallback(() => {
		handleCbccBack(clientId);
	}, [clientId]);

	return { handleBack, isForeign };
}
